import React, { useEffect, useRef } from 'react'
import { FlowInner, FlowOuter } from './flow.styled'
import { useStepComponent, useStepLayoutComponent } from './flow.utils'
import useConfig from '../../../config/state/use-config'
import StepLayout from '../step-layout/step-layout'
import useFlow from '../../state/use-flow'
import useTrackPageView from '../../../analytics/hooks/useTrackPageView'
import useTrackFunnelEvent from '../../../analytics/hooks/useTrackFunnelEvent'
import mParticleSdk from '@mparticle/web-sdk'
import { useTrackedExperiment } from '../../../optimizely'
import { OptimizelyFeatureFlags } from 'models/Optimizely'
import { isReferrerFromWhammyDonatePage } from 'utils/functions/isReferrerFromWhammyDonatePage'

const Flow = React.memo(() => {
  const config = useConfig()
  const flow = useFlow()

  // Get tracking functions
  const trackPageView = useTrackPageView()
  const trackFunnelEvent = useTrackFunnelEvent()

  const isSinglePageFlow = !config.isEmbedded

  // These calls are purely for tracking the experiment_viewed events for new experiences,
  // as the routing decisions were already made.
  useTrackedExperiment(
    OptimizelyFeatureFlags.npo_checkout_app_2025,
    'NPO Checkout App 2025',
    isSinglePageFlow,
  )

  useTrackedExperiment(
    OptimizelyFeatureFlags.npo_checkout_app_2025_aa,
    'NPO Checkout App 2025 AA',
    isSinglePageFlow,
  )

  // Track if app open event has been fired
  const appOpenTrackedRef = useRef(false)

  const StepComponent = useStepComponent()
  const StepLayoutComponent = useStepLayoutComponent()

  const isRedirectedFromWhammy = isReferrerFromWhammyDonatePage()

  // Track app open when Flow component first mounts and analytics are ready
  useEffect(() => {
    if (!appOpenTrackedRef.current && trackFunnelEvent && !isRedirectedFromWhammy) {
      const success = trackFunnelEvent(
        'Donation Funnel Start Viewed',
        mParticleSdk.EventType.Transaction,
      )

      if (success) {
        appOpenTrackedRef.current = true
      }
    }
  }, [isRedirectedFromWhammy, trackFunnelEvent])

  // Call trackPageView when the current step changes or analytics dependencies become available
  useEffect(() => {
    if (flow.currentStep?.name) {
      trackPageView(flow.currentStep.name)
    }
  }, [flow.currentStep?.name, trackPageView]) // Include trackPageView to ensure tracking happens when analytics dependencies are ready

  return config.isEmbedded ? (
    <FlowOuter>
      <FlowInner>
        <StepLayoutComponent>
          <StepComponent />
        </StepLayoutComponent>
      </FlowInner>
    </FlowOuter>
  ) : (
    <StepLayout.Root>
      <StepLayoutComponent>
        <StepComponent />
      </StepLayoutComponent>
    </StepLayout.Root>
  )
})

Flow.displayName = 'FlowComponent'

export default Flow
